/**
 * Recommendation Tracker - Fire-and-forget interaction tracking
 *
 * Sends user interactions to the recommendation engine backend.
 * Never blocks UI, never throws exceptions.
 */

import { authFetch } from '@/lib/authFetch';

export type InteractionType =
  | 'view'
  | 'view_detail'
  | 'search_click'
  | 'watchlist_add'
  | 'watchlist_remove'
  | 'post_like';

export type InteractionSource =
  | 'feed'
  | 'for_you'
  | 'trending'
  | 'search'
  | 'direct'
  | 'notification';

const NAV_SOURCE_KEY = 'frenflow-rec-nav-source';

// ============================================================================
// Navigation Source (sessionStorage)
// ============================================================================

/**
 * Store where the user is navigating FROM (e.g., 'feed', 'for_you', 'trending').
 * Called when user clicks a card to go to a detail page.
 * The detail page reads this to attribute the source correctly.
 */
export function setNavigationSource(source: InteractionSource): void {
  try {
    sessionStorage.setItem(NAV_SOURCE_KEY, source);
  } catch {
    // SSR or storage full - ignore
  }
}

/**
 * Read and clear the navigation source. Called once by the detail page on mount.
 * Returns null if no source was set (direct navigation).
 */
export function getAndClearNavigationSource(): InteractionSource | null {
  try {
    const source = sessionStorage.getItem(NAV_SOURCE_KEY) as InteractionSource | null;
    if (source) sessionStorage.removeItem(NAV_SOURCE_KEY);
    return source;
  } catch {
    return null;
  }
}

// ============================================================================
// View Impression Queue (batched to prevent 429 bursts from IntersectionObserver)
// ============================================================================

const viewQueue: Array<{ slug: string; source: InteractionSource }> = [];
const viewSent = new Set<string>(); // dedupe across queue flushes
let flushTimer: ReturnType<typeof setTimeout> | null = null;

/**
 * Flush all accumulated view impressions in a single batch request.
 * Debounced: waits 2s after the last enqueued view before flushing.
 */
function flushViewQueue() {
  flushTimer = null;
  if (viewQueue.length === 0) return;

  // Drain the entire queue into a local snapshot
  const batch = viewQueue.splice(0, viewQueue.length);

  const events = batch.map((item) => ({
    marketSlug: item.slug,
    interactionType: 'view' as const,
    source: item.source,
  }));

  authFetch('/api/recommendations/track-batch', {
    method: 'POST',
    body: JSON.stringify({ events }),
  }).catch(() => {});
}

// ============================================================================
// Core Tracking
// ============================================================================

/**
 * Track a user interaction with a market (fire-and-forget)
 */
export function trackRecommendation(
  marketSlug: string,
  interactionType: InteractionType,
  source?: InteractionSource,
  viewDurationMs?: number,
): void {
  if (!marketSlug) return;

  // Batch 'view' impressions to prevent 429 bursts on page load / scroll
  if (interactionType === 'view') {
    const key = `${marketSlug}:${source || 'feed'}`;
    if (viewSent.has(key)) return;
    viewSent.add(key);
    viewQueue.push({ slug: marketSlug, source: source || 'feed' });
    // Debounce: reset the 2s timer on every new view so we accumulate a full batch
    if (flushTimer) clearTimeout(flushTimer);
    flushTimer = setTimeout(flushViewQueue, 2000);
    return;
  }

  const body: Record<string, unknown> = { marketSlug, interactionType, source };
  if (viewDurationMs && viewDurationMs > 0) {
    body.viewDurationMs = Math.min(viewDurationMs, 600000); // Cap at 10 min
  }

  authFetch('/api/recommendations/track', {
    method: 'POST',
    body: JSON.stringify(body),
  }).catch(() => {});
}

/**
 * Record a click on a recommended market (updates impression feedback loop)
 */
export function trackRecommendationClick(marketSlug: string): void {
  if (!marketSlug) return;

  // Track as view_detail which triggers recordClick in backend
  authFetch('/api/recommendations/track', {
    method: 'POST',
    body: JSON.stringify({ marketSlug, interactionType: 'view_detail', source: 'for_you' }),
  }).catch(() => {});
}

/**
 * Submit negative feedback (hide / not interested)
 */
export function trackRecommendationFeedback(
  marketSlug: string,
  action: 'hide' | 'not_interested' | 'already_seen' | 'spam' = 'not_interested',
): void {
  if (!marketSlug) return;

  authFetch('/api/recommendations/feedback', {
    method: 'POST',
    body: JSON.stringify({ marketSlug, action }),
  }).catch(() => {});
}

// ============================================================================
// Dwell Time Tracking
// ============================================================================

/**
 * Send dwell time for a market/event page visit.
 * Called from useEffect cleanup (React navigation) or visibilitychange.
 * Uses authFetch which requires Privy token (works for in-app navigation).
 */
export function trackDwellTime(
  marketSlug: string,
  durationMs: number,
  source?: InteractionSource,
): void {
  if (!marketSlug || durationMs < 2000) return; // Ignore <2s visits

  authFetch('/api/recommendations/track', {
    method: 'POST',
    body: JSON.stringify({
      marketSlug,
      interactionType: 'view_detail',
      source: source || 'direct',
      viewDurationMs: Math.min(Math.round(durationMs), 600000),
    }),
  }).catch(() => {});
}
